// Site footer. The block at its top (call-to-action cards) varies by page; see FooterCtas.tsx.
// Converted from the Webflow footer symbols.

import type { ReactNode } from 'react';
import { SlideIn } from '@components/interactions';
import { NavLink } from '@components/webflow';

declare global {
  interface Window {
    // Cookiebot, the cookie-consent banner, which Google Tag Manager loads (see index.html).
    Cookiebot?: { renew: () => void };
  }
}

/** Reopens the Cookiebot banner so visitors can change their cookie choices. */
function CookieSettingsLink({ className, children }: { className: string; children: ReactNode }) {
  return (
    <a
      href="#"
      className={className}
      onClick={(e) => {
        e.preventDefault();
        window.Cookiebot?.renew();
      }}
    >
      {children}
    </a>
  );
}

export function SiteFooter({ cta }: { cta: ReactNode }) {
  return (
    <div>
      <div className="footer-container">
        <div className="footer-link-wrapper">
          <div className="footer-description-container">
            <img
              src="/assets/images/67241465129b580e4b924af4_Vectors-Wrapper.svg"
              width="108"
              alt="DBOS - Logo"
              className="navigation-logo"
            />
            <div className="text-body text-alignment-left">
              DBOS radically simplifies cloud application devops and deployment.
            </div>
            <div className="footer-link-socials">
              <a
                href="https://www.linkedin.com/company/dbos-inc/"
                target="_blank"
                className="social-link-wrapper w-inline-block"
              >
                <div className="social-link">
                  <div className="social-link-code w-embed">
                    <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {'\n'}
                      <path
                        d="M0.5 1.61369C0.5 1.22889 0.635139 0.911425 0.905405 0.661313C1.17567 0.411189 1.52703 0.286133 1.95946 0.286133C2.38417 0.286133 2.7278 0.409261 2.99035 0.655541C3.26061 0.909509 3.39575 1.24043 3.39575 1.64833C3.39575 2.01773 3.26448 2.32557 3.00193 2.57185C2.73166 2.82581 2.37645 2.9528 1.93629 2.9528H1.92471C1.5 2.9528 1.15637 2.82581 0.893822 2.57185C0.63127 2.31788 0.5 1.99849 0.5 1.61369ZM0.650579 11.7147V4.0033H3.22201V11.7147H0.650579ZM4.64672 11.7147H7.21815V7.40878C7.21815 7.13942 7.24904 6.93162 7.31081 6.78541C7.41891 6.52374 7.58301 6.30248 7.80309 6.12163C8.02317 5.94077 8.29922 5.85034 8.63127 5.85034C9.49614 5.85034 9.92857 6.43139 9.92857 7.59349V11.7147H12.5V7.29334C12.5 6.15433 12.2297 5.29046 11.6892 4.70172C11.1486 4.11297 10.4344 3.8186 9.54633 3.8186C8.55019 3.8186 7.77413 4.24573 7.21815 5.09998V5.12307H7.20656L7.21815 5.09998V4.0033H4.64672C4.66216 4.24957 4.66988 5.01532 4.66988 6.30056C4.66988 7.58579 4.66216 9.3905 4.64672 11.7147Z"
                        fill="currentColor"
                      />
                      {'\n'}
                    </svg>
                  </div>
                </div>
              </a>
              <a href="https://github.com/dbos-inc" target="_blank" className="social-link-wrapper w-inline-block">
                <div className="social-link">
                  <div className="social-link-code w-embed">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {'\n'}
                      <path
                        d="M6.00002 0.202148C2.68673 0.202148 0 2.88841 0 6.20217C0 8.85318 1.71919 11.1022 4.1032 11.8956C4.40304 11.9511 4.51316 11.7655 4.51316 11.607C4.51316 11.4639 4.50756 10.9912 4.50502 10.4899C2.83576 10.8528 2.48354 9.78195 2.48354 9.78195C2.2106 9.08842 1.81735 8.90405 1.81735 8.90405C1.27299 8.53164 1.85838 8.53927 1.85838 8.53927C2.4609 8.58162 2.77816 9.1576 2.77816 9.1576C3.31329 10.0749 4.18178 9.80967 4.52417 9.6564C4.578 9.26856 4.73353 9.00391 4.9051 8.85408C3.57244 8.70232 2.17145 8.18784 2.17145 5.88882C2.17145 5.23378 2.40584 4.69851 2.78969 4.27833C2.72739 4.12719 2.52203 3.51695 2.84781 2.69048C2.84781 2.69048 3.35165 2.52922 4.49824 3.30552C4.97681 3.17254 5.49011 3.1059 6.00002 3.10365C6.50993 3.1059 7.02361 3.17254 7.50312 3.30552C8.64835 2.52922 9.15149 2.69048 9.15149 2.69048C9.47807 3.51695 9.27261 4.12719 9.21031 4.27833C9.595 4.69851 9.8278 5.23374 9.8278 5.88882C9.8278 8.1933 8.42417 8.70077 7.08813 8.84928C7.30332 9.03548 7.49508 9.40065 7.49508 9.96039C7.49508 10.7632 7.48811 11.4093 7.48811 11.607C7.48811 11.7666 7.59611 11.9537 7.90029 11.8948C10.283 11.1005 12 8.85229 12 6.20217C12 2.88841 9.31365 0.202148 6.00002 0.202148Z"
                        fill="currentColor"
                      />
                      {'\n'}
                    </svg>
                  </div>
                </div>
              </a>
              <a
                href="https://discord.com/invite/jsmC6pXGgX"
                target="_blank"
                className="social-link-wrapper w-inline-block"
              >
                <div className="social-link">
                  <div className="social-link-code w-embed">
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {'\n'}
                      <path
                        d="M10.174 0.974379C11.5292 2.96698 12.1985 5.21457 11.9483 7.80207C11.9473 7.81302 11.9416 7.82307 11.9326 7.82967C10.9063 8.58327 9.91194 9.04062 8.93154 9.34391C8.92391 9.34623 8.91574 9.3461 8.90818 9.34355C8.90062 9.34099 8.89405 9.33614 8.88939 9.32967C8.66289 9.01451 8.45709 8.68227 8.27679 8.33337C8.26644 8.31282 8.27589 8.28807 8.29719 8.27997C8.62404 8.15682 8.93484 8.00922 9.23379 7.83447C9.25734 7.82067 9.25884 7.78692 9.23709 7.77072C9.17364 7.72362 9.11079 7.67412 9.05064 7.62462C9.03939 7.61547 9.02424 7.61367 9.01149 7.61982C7.07064 8.51607 4.94455 8.51607 2.98075 7.61982C2.968 7.61412 2.95285 7.61607 2.9419 7.62507C2.8819 7.67457 2.8189 7.72362 2.75605 7.77072C2.7343 7.78692 2.7361 7.82067 2.7598 7.83447C3.05875 8.00592 3.36955 8.15682 3.69595 8.28057C3.7171 8.28867 3.72715 8.31282 3.71665 8.33337C3.54025 8.68272 3.33445 9.01497 3.10375 9.33011C3.0937 9.34286 3.0772 9.34871 3.0616 9.34391C2.08585 9.04062 1.0915 8.58327 0.0652053 7.82967C0.0566553 7.82307 0.0505053 7.81257 0.0496053 7.80162C-0.159494 5.56347 0.266655 3.29728 1.8223 0.973929C1.82605 0.967779 1.83175 0.962979 1.83835 0.960129C2.6038 0.60883 3.42385 0.35038 4.28095 0.202781C4.29655 0.200381 4.31215 0.207581 4.32025 0.221381C4.42615 0.40888 4.5472 0.64933 4.6291 0.84583C5.53255 0.70783 6.45009 0.70783 7.37244 0.84583C7.45434 0.65353 7.57119 0.40888 7.67664 0.221381C7.6804 0.214535 7.68622 0.20905 7.69328 0.20571C7.70033 0.202369 7.70827 0.201344 7.71594 0.202781C8.57349 0.35083 9.39354 0.60928 10.1584 0.960129C10.1651 0.962979 10.1707 0.967779 10.174 0.974379ZM5.0884 5.22987C5.09785 4.56822 4.61545 4.02072 4.0099 4.02072C3.4093 4.02072 2.93155 4.56342 2.93155 5.22987C2.93155 5.89617 3.41875 6.43887 4.0099 6.43887C4.61065 6.43887 5.0884 5.89617 5.0884 5.22987ZM9.07569 5.22987C9.08514 4.56822 8.60274 4.02072 7.99734 4.02072C7.39659 4.02072 6.91884 4.56342 6.91884 5.22987C6.91884 5.89617 7.40604 6.43887 7.99734 6.43887C8.60274 6.43887 9.07569 5.89617 9.07569 5.22987Z"
                        fill="currentColor"
                      />
                      {'\n'}
                    </svg>
                  </div>
                </div>
              </a>
              <a href="https://twitter.com/DBOS_Inc" target="_blank" className="social-link-wrapper w-inline-block">
                <div className="social-link">
                  <div className="social-link-code w-embed">
                    <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {'\n'}
                      <g clipPath="url(#clip0_259_5042)">
                        {'\n'}
                        <path
                          d="M6.54209 4.86042L10.6343 0.202148H9.66495L6.11015 4.24603L3.27306 0.202148H0L4.29118 6.3178L0 11.2021H0.969389L4.72092 6.93072L7.71774 11.2021H10.9908M1.31926 0.918016H2.80852L9.66421 10.5214H8.17459"
                          fill="currentColor"
                        />
                        {'\n'}
                      </g>
                      {'\n'}
                      <defs>
                        {'\n'}
                        <clipPath id="clip0_259_5042">
                          {'\n'}
                          <rect width="10.9908" height="11" fill="currentColor" transform="translate(0 0.202148)" />
                          {'\n'}
                        </clipPath>
                        {'\n'}
                      </defs>
                      {'\n'}
                    </svg>
                  </div>
                </div>
              </a>
              <NavLink href="/contact" className="social-link-wrapper w-inline-block">
                <div className="social-link">
                  <div className="social-link-code w-embed">
                    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {'\n'}
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.132783 1.20668C0.0473155 1.4064 0 1.62634 0 1.85732V8.8918C0 9.80596 0.741046 10.547 1.65517 10.547H10.3448C11.259 10.547 12 9.80596 12 8.8918V1.85732C12 1.62634 11.9527 1.4064 11.8672 1.20669C11.8445 1.23545 11.819 1.26261 11.7906 1.28774L6.41126 6.04636C6.17644 6.25409 5.82358 6.25409 5.58875 6.04636L0.209444 1.28774C0.181032 1.26261 0.15547 1.23545 0.132783 1.20668ZM1.00444 0.334971C1.01377 0.342323 1.02294 0.349982 1.03195 0.357951L6.00001 4.75277L10.9681 0.357951C10.9771 0.349983 10.9862 0.342323 10.9956 0.334972C10.7958 0.249479 10.5759 0.202148 10.3448 0.202148H1.65517C1.42415 0.202148 1.20418 0.249479 1.00444 0.334971Z"
                        fill="currentColor"
                      />
                      {'\n'}
                    </svg>
                  </div>
                </div>
              </NavLink>
            </div>
          </div>
          <div className="footer-column">
            <div className="footer-column-title">Products</div>
            <div className="footer-column-group">
              <NavLink href="/dbos-cloud" className="footer-link-item">
                {'DBOS\u00a0Cloud'}
              </NavLink>
              <NavLink href="/dbos-transact" className="footer-link-item">
                {'DBOS\u00a0Transact'}
              </NavLink>
              <NavLink href="/dbos-pricing" className="footer-link-item">
                Pricing Plans
              </NavLink>
              <NavLink href="/contact" className="footer-link-item">
                Contact Us
              </NavLink>
            </div>
          </div>
          <div className="footer-column">
            <div className="footer-column-title">Solutions</div>
            <div className="footer-column-group">
              <NavLink href="/solutions/cron-hosting" className="footer-link-item">
                Cron Job Platform
              </NavLink>
              <NavLink href="/solutions/agentic-ai-platform" className="footer-link-item">
                Durable AI Workflows
              </NavLink>
              <NavLink href="/solutions/data-pipeline-platform" className="footer-link-item">
                Durable Data Pipelines
              </NavLink>
              <NavLink href="/solutions/cloud-modernization" className="footer-link-item">
                Cloud Modernization
              </NavLink>
            </div>
          </div>
          <div className="footer-column">
            <div className="footer-column-title">Developers</div>
            <div className="footer-column-group">
              <a href="https://docs.dbos.dev/" className="footer-link-item">
                Docs
              </a>
              <a href="https://docs.dbos.dev/quickstart" className="footer-link-item">
                Quickstart Guide
              </a>
              <a href="https://docs.dbos.dev/examples" className="footer-link-item">
                Examples
              </a>
              <a href="https://docs.dbos.dev/cloud-tutorials/application-management" className="footer-link-item">
                Tutorials
              </a>
            </div>
          </div>
          <div className="footer-column">
            <div className="footer-column-title">Company</div>
            <div className="footer-column-group">
              <NavLink href="/about" className="footer-link-item">
                About Us
              </NavLink>
              <NavLink href="/privacy" className="footer-link-item">
                Privacy Policy
              </NavLink>
              <NavLink href="/terms-of-service" className="footer-link-item">
                Terms of Service
              </NavLink>
              <CookieSettingsLink className="footer-link-item">Cookies</CookieSettingsLink>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="text-body">Copyright © DBOS, Inc. 2025</div>
        </div>
      </div>
      <footer className="footer5_component">
        <SlideIn className="padding-global" as="div">
          <div className="container-large">
            <div className="padding-vertical padding-section-large">
              {cta}
              <div className="padding-bottom padding-xxlarge">
                <div className="w-layout-grid footer5_top-wrapper">
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Products</div>
                    </div>
                    <div className="footer5_link-list">
                      <NavLink href="/dbos-transact" className="footer5_link">
                        {'DBOS\u00a0Transact'}
                      </NavLink>
                      <NavLink href="/dbos-conductor" className="footer5_link">
                        DBOS Conductor
                      </NavLink>
                      <NavLink href="/dbos-pricing" className="footer5_link">
                        Pricing Plans
                      </NavLink>
                      <NavLink href="/compare/dbos-vs-temporal" className="footer5_link">
                        DBOS vs. Temporal
                      </NavLink>
                    </div>
                  </div>
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Use Cases</div>
                    </div>
                    <div className="footer5_link-list">
                      <a href="/solutions/cron-hosting" className="footer5_link">
                        Cron Job Platform
                      </a>
                      <a href="/solutions/agentic-ai-platform" className="footer5_link">
                        {'Durable AI\u00a0Workflows'}
                      </a>
                      <a href="/solutions/data-pipeline-platform" className="footer5_link">
                        Durable Data Pipelines
                      </a>
                      <a href="/solutions/cloud-modernization" className="footer5_link">
                        Cloud Modernization
                      </a>
                    </div>
                  </div>
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Customer Stories</div>
                    </div>
                    <div className="footer5_link-list">
                      <a href="/case-studies/bms" className="footer5_link">
                        Bristol Myers Squibb
                      </a>
                      <a href="/case-studies/yutori-large-scale-durable-agentic-ai" className="footer5_link">
                        Yutori
                      </a>
                      <a href="/case-studies/dosu" className="footer5_link">
                        Dosu
                      </a>
                      <NavLink href="/customer-stories" className="footer5_link">
                        All stories
                      </NavLink>
                    </div>
                  </div>
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Developers</div>
                    </div>
                    <div className="footer5_link-list">
                      <a href="https://docs.dbos.dev/" className="footer5_link">
                        Docs
                      </a>
                      <a href="https://docs.dbos.dev/quickstart" className="footer5_link">
                        Quickstart Guide
                      </a>
                      <a href="https://docs.dbos.dev/python/examples/widget-store" className="footer5_link">
                        Examples
                      </a>
                      <a href="https://docs.dbos.dev/python/tutorials/workflow-tutorial" className="footer5_link">
                        Tutorials
                      </a>
                      <a href="/llms.txt" className="footer5_link">
                        LLMs.txt
                      </a>
                    </div>
                  </div>
                  <div className="footer5_link-column">
                    <div className="margin-bottom margin-xsmall">
                      <div className="text-weight-semibold">Company</div>
                    </div>
                    <div className="footer5_link-list">
                      <NavLink href="/contact" className="footer5_link">
                        Contact Sales
                      </NavLink>
                      <NavLink href="/blog" className="footer5_link">
                        Blog
                      </NavLink>
                      <NavLink href="/about" className="footer5_link">
                        About Us
                      </NavLink>
                      <a href="/about#team" target="_blank" className="footer5_link">
                        Our Team
                      </a>
                      <NavLink href="/partners" className="footer5_link">
                        Partners
                      </NavLink>
                      <NavLink href="/jobs" className="footer5_link">
                        Careers
                      </NavLink>
                    </div>
                  </div>
                </div>
              </div>
              <div className="margin-vertical margin-large">
                <div className="divider-horizontal" />
              </div>
              <div className="padding-top padding-medium">
                <div className="footer5_bottom-wrapper">
                  <NavLink
                    href="/"
                    id="w-node-_10146262-347e-bc86-a6c8-5cc4f0f78697-351fb178"
                    className="footer5_logo-link w-nav-brand"
                  >
                    <img
                      width="108"
                      src="/assets/images/67241465129b580e4b924af4_Vectors-Wrapper.svg"
                      alt="DBOS - Logo"
                      className="navigation-logo-new"
                    />
                  </NavLink>
                  <div className="w-layout-grid footer5_legal-list">
                    <div id="w-node-_10146262-347e-bc86-a6c8-5cc4f0f7869a-351fb178" className="footer5_credit-text">
                      {'© 2026 DBOS, Inc. '}
                    </div>
                    <NavLink href="/privacy" className="footer5_legal-link">
                      Privacy Policy
                    </NavLink>
                    <NavLink href="/terms-of-service" className="footer5_legal-link">
                      Terms of Service
                    </NavLink>
                    <NavLink href="/opn-pledge" className="footer5_legal-link">
                      Open Patent Pledge
                    </NavLink>
                    <CookieSettingsLink className="footer5_legal-link">Cookies Settings</CookieSettingsLink>
                  </div>
                  <div className="w-layout-grid footer5_social-icons">
                    <a href="https://discord.com/invite/jsmC6pXGgX" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            d="M14.2524 6.66699C14.096 6.94165 13.9556 7.22579 13.828 7.51624C12.6154 7.33629 11.3806 7.33629 10.1649 7.51624C10.0404 7.22579 9.89682 6.94165 9.74048 6.66699C8.60133 6.85958 7.49091 7.19738 6.43793 7.67409C4.3511 10.7333 3.78631 13.7136 4.06711 16.6528C5.28921 17.5462 6.6581 18.2282 8.11632 18.6638C8.44498 18.2282 8.73535 17.7641 8.98424 17.281C8.51199 17.1074 8.05569 16.8896 7.61855 16.637C7.73341 16.5549 7.8451 16.4697 7.95358 16.3876C10.5159 17.581 13.4834 17.581 16.0488 16.3876C16.1573 16.476 16.269 16.5612 16.3839 16.637C15.9467 16.8927 15.4904 17.1074 15.015 17.2842C15.2639 17.7672 15.5542 18.2313 15.8829 18.667C17.3411 18.2313 18.71 17.5525 19.9322 16.6591C20.2639 13.2495 19.3641 10.2945 17.5548 7.67725C16.5051 7.20054 15.3947 6.86273 14.2555 6.6733L14.2524 6.66699ZM9.34161 14.8438C8.55348 14.8438 7.89935 14.1366 7.89935 13.2621C7.89935 12.3876 8.52794 11.6773 9.33843 11.6773C10.1489 11.6773 10.7935 12.3908 10.7807 13.2621C10.7679 14.1335 10.1457 14.8438 9.34161 14.8438ZM14.6576 14.8438C13.8663 14.8438 13.2185 14.1366 13.2185 13.2621C13.2185 12.3876 13.8471 11.6773 14.6576 11.6773C15.4681 11.6773 16.1094 12.3908 16.0967 13.2621C16.0839 14.1335 15.4617 14.8438 14.6576 14.8438Z"
                            fill="currentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://github.com/dbos-inc" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12.0074 4.66699C7.5968 4.66699 4.03125 8.33366 4.03125 12.8698C4.03125 16.4958 6.31582 19.5652 9.48513 20.6515C9.88137 20.7332 10.0265 20.475 10.0265 20.2578C10.0265 20.0677 10.0135 19.4158 10.0135 18.7367C7.79468 19.2257 7.33264 17.7588 7.33264 17.7588C6.97607 16.8082 6.44774 16.5638 6.44774 16.5638C5.72154 16.0613 6.50064 16.0613 6.50064 16.0613C7.30619 16.1157 7.72888 16.9033 7.72888 16.9033C8.44186 18.1527 9.59076 17.7997 10.053 17.5823C10.1189 17.0527 10.3304 16.686 10.5548 16.4823C8.78521 16.2922 6.92333 15.586 6.92333 12.4352C6.92333 11.5388 7.24007 10.8055 7.74194 10.2352C7.66276 10.0315 7.38537 9.18933 7.82129 8.06216C7.82129 8.06216 8.49476 7.84483 10.0133 8.90416C10.6634 8.7246 11.3339 8.63326 12.0074 8.63249C12.6809 8.63249 13.3674 8.72766 14.0014 8.90416C15.5201 7.84483 16.1935 8.06216 16.1935 8.06216C16.6295 9.18933 16.3519 10.0315 16.2727 10.2352C16.7878 10.8055 17.0915 11.5388 17.0915 12.4352C17.0915 15.586 15.2296 16.2785 13.4468 16.4823C13.7374 16.7403 13.9881 17.2292 13.9881 18.0033C13.9881 19.1033 13.9751 19.9862 13.9751 20.2577C13.9751 20.475 14.1204 20.7332 14.5165 20.6517C17.6858 19.565 19.9704 16.4958 19.9704 12.8698C19.9834 8.33366 16.4048 4.66699 12.0074 4.66699Z"
                            fill="currentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://twitter.com/DBOS_Inc" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            d="M17.1761 4H19.9362L13.9061 10.7774L21 20H15.4456L11.0951 14.4066L6.11723 20H3.35544L9.80517 12.7508L3 4H8.69545L12.6279 9.11262L17.1761 4ZM16.2073 18.3754H17.7368L7.86441 5.53928H6.2232L16.2073 18.3754Z"
                            fill="CurrentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://www.linkedin.com/company/dbos-inc/" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.5 3C3.67157 3 3 3.67157 3 4.5V19.5C3 20.3284 3.67157 21 4.5 21H19.5C20.3284 21 21 20.3284 21 19.5V4.5C21 3.67157 20.3284 3 19.5 3H4.5ZM8.52076 7.00272C8.52639 7.95897 7.81061 8.54819 6.96123 8.54397C6.16107 8.53975 5.46357 7.90272 5.46779 7.00413C5.47201 6.15897 6.13998 5.47975 7.00764 5.49944C7.88795 5.51913 8.52639 6.1646 8.52076 7.00272ZM12.2797 9.76176H9.75971H9.7583V18.3216H12.4217V18.1219C12.4217 17.742 12.4214 17.362 12.4211 16.9819V16.9818V16.9816V16.9815V16.9812C12.4203 15.9674 12.4194 14.9532 12.4246 13.9397C12.426 13.6936 12.4372 13.4377 12.5005 13.2028C12.7381 12.3253 13.5271 11.7586 14.4074 11.8979C14.9727 11.9864 15.3467 12.3141 15.5042 12.8471C15.6013 13.1803 15.6449 13.5389 15.6491 13.8863C15.6605 14.9339 15.6589 15.9815 15.6573 17.0292V17.0294C15.6567 17.3992 15.6561 17.769 15.6561 18.1388V18.3202H18.328V18.1149C18.328 17.6629 18.3278 17.211 18.3275 16.7591V16.759V16.7588C18.327 15.6293 18.3264 14.5001 18.3294 13.3702C18.3308 12.8597 18.276 12.3563 18.1508 11.8627C17.9638 11.1286 17.5771 10.5211 16.9485 10.0824C16.5027 9.77019 16.0133 9.5691 15.4663 9.5466C15.404 9.54401 15.3412 9.54062 15.2781 9.53721L15.2781 9.53721L15.2781 9.53721C14.9984 9.52209 14.7141 9.50673 14.4467 9.56066C13.6817 9.71394 13.0096 10.0641 12.5019 10.6814C12.4429 10.7522 12.3852 10.8241 12.2991 10.9314L12.2991 10.9315L12.2797 10.9557V9.76176ZM5.68164 18.3244H8.33242V9.76733H5.68164V18.3244Z"
                            fill="CurrentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                    <a href="https://www.youtube.com/@DBOS-Inc" className="footer5_social-link w-inline-block">
                      <div className="icon-embed-xsmall w-embed">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {'\n'}
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M20.5686 4.77345C21.5163 5.02692 22.2555 5.76903 22.5118 6.71673C23.1821 9.42042 23.1385 14.5321 22.5259 17.278C22.2724 18.2257 21.5303 18.965 20.5826 19.2213C17.9071 19.8831 5.92356 19.8015 3.40294 19.2213C2.45524 18.9678 1.71595 18.2257 1.45966 17.278C0.827391 14.7011 0.871044 9.25144 1.44558 6.73081C1.69905 5.78311 2.44116 5.04382 3.38886 4.78753C6.96561 4.0412 19.2956 4.282 20.5686 4.77345ZM9.86682 8.70227L15.6122 11.9974L9.86682 15.2925V8.70227Z"
                            fill="CurrentColor"
                          />
                          {'\n'}
                        </svg>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SlideIn>
      </footer>
    </div>
  );
}
